import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, forwardRef, inject, input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { type ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms'
import { type MatFormFieldAppearance, MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { ApiService } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { orderBy } from 'lodash'
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search'
import { combineLatest, map, type Observable, shareReplay, startWith } from 'rxjs'

import { SelectAllOptionComponent } from '../select-all-option'

/**
 * Reusable, searchable multi-select for product (procedure) categories.
 *
 * Self-contained (renders its own `<mat-form-field>`) and integrates with
 * reactive forms via `ControlValueAccessor`, so it drops into any form with
 * `formControlName`. Its value is an array of category ids — the same ids
 * stored in an appointment's `productCategories`.
 *
 * NOTE: nearly identical to `CarrierMultiSelectComponent`; a shared abstract
 * base class would remove the duplication (future refactor).
 */
@Component({
  selector: 'mmx-category-multi-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './category-multi-select.component.html',
  styleUrls: ['./category-multi-select.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    TranslateModule,
    SelectAllOptionComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CategoryMultiSelectComponent),
      multi: true,
    },
  ],
})
export class CategoryMultiSelectComponent implements ControlValueAccessor {
  private readonly apiService = inject(ApiService)

  readonly selectControl = new FormControl<string[]>([], { nonNullable: true })
  readonly filterControl = new FormControl<string>('', { nonNullable: true })
  readonly labelKey = input('category-multi-select.label')
  readonly appearance = input<MatFormFieldAppearance>('fill')

  private readonly categories$ = this.apiService.getProductCategories().pipe(
    // Match week-view: don't offer categories hidden from the UI.
    map((categories) => orderBy(categories.filter((category) => !category.hidden), 'label')),
    shareReplay(1),
  )

  readonly categoryIds$: Observable<string[]> = this.categories$.pipe(
    map((categories) => categories.map((category) => category.id)),
  )

  readonly filteredCategories$ = combineLatest([
    this.categories$,
    this.filterControl.valueChanges.pipe(startWith('')),
  ]).pipe(
    map(([categories, term]) => {
      const query = term.toLowerCase().trim()
      return query
        ? categories.filter((category) => (category.label ?? '').toLowerCase().includes(query))
        : categories
    }),
  )

  private onChange: (value: string[]) => void = () => { /* registered by forms */ }
  onTouched: () => void = () => { /* registered by forms */ }

  constructor() {
    this.selectControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.onChange(value))
  }

  writeValue(value: string[] | null): void {
    this.selectControl.setValue(value ?? [], { emitEvent: false })
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.selectControl.disable({ emitEvent: false })
    } else {
      this.selectControl.enable({ emitEvent: false })
    }
  }
}
